// src/components/kit/epk/EpkReportModal.tsx

import { useState } from 'react';
import { reportEpk } from '@/api/epkPublic';

interface EpkReportModalProps {
  slug: string;
  onClose: () => void;
}

const REASONS = [
  { value: 'offensive_content', label: 'Offensive content' },
  { value: 'spam', label: 'Spam' },
  { value: 'impersonation', label: 'Impersonation' },
  { value: 'other', label: 'Other' },
];

export default function EpkReportModal({ slug, onClose }: EpkReportModalProps) {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'submitted' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason) return;

    setStatus('submitting');
    try {
      await reportEpk(slug, reason, details || undefined);
      setStatus('submitted');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        {status === 'submitted' ? (
          <div className="text-center">
            <p className="text-lg font-semibold text-gray-900">Report Submitted</p>
            <p className="mt-2 text-sm text-gray-500">Thank you for your report. We'll review it shortly.</p>
            <button onClick={onClose} className="mt-4 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700">Close</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <h2 className="text-lg font-semibold text-gray-900">Report this page</h2>
            <p className="mt-1 text-sm text-gray-500">Let us know why you think this page violates our terms.</p>

            <div className="mt-4 space-y-2">
              {REASONS.map((r) => (
                <label key={r.value} className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors ${reason === r.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                  <input type="radio" name="reason" value={r.value} checked={reason === r.value} onChange={(e) => setReason(e.target.value)} className="accent-blue-500" />
                  <span className="text-sm text-gray-700">{r.label}</span>
                </label>
              ))}
            </div>

            <textarea value={details} onChange={(e) => setDetails(e.target.value)} placeholder="Additional details (optional)" maxLength={1000} rows={3} className="mt-4 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 placeholder-gray-400 focus:border-blue-500 focus:outline-none" />

            {status === 'error' && <p className="mt-2 text-sm text-red-500">Something went wrong. Please try again.</p>}

            <div className="mt-4 flex gap-2">
              <button type="submit" disabled={!reason || status === 'submitting'} className="flex-1 rounded-lg bg-gray-900 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-700 disabled:opacity-50">
                {status === 'submitting' ? 'Submitting...' : 'Submit Report'}
              </button>
              <button type="button" onClick={onClose} className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50">Cancel</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
